/* Keep the native product purchase controls easy to reach on narrow screens. */
(() => {
  document.addEventListener('error', (event) => {
    const image = event.target;
    if (!(image instanceof HTMLImageElement) || !image.matches('.co-video-card__poster img')) return;
    image.hidden = true;
    image.closest('.co-video-card__poster')?.classList.add('co-video-card__poster--fallback');
  }, true);

  const initRouteFallback = () => {
    const root = document.querySelector('[data-co-route-fallback]');
    if (!root || root.dataset.coRouteReady) return;
    const normalizePath = (value) => (value || '/').replace(/\/+$/, '') || '/';
    const currentPath = normalizePath(window.location.pathname.toLowerCase());
    const homePath = normalizePath(root.dataset.coHomePath || '/');
    const collectionMatch = currentPath.match(/(?:^|\/)collections\/([^/]+)/);
    let activeView = 'not-found';

    if (collectionMatch) {
      let handle = collectionMatch[1];
      try { handle = decodeURIComponent(handle); } catch (_) { /* Keep the original URL segment. */ }
      if (['all', 'hearing-aids', 'ite-hearing-amplifier', 'bte-hearing-amplifier'].includes(handle)) {
        activeView = 'hearing-collection';
        if (handle === 'all') {
          const modelsHeading = root.querySelector('#co-route-fallback-models-title');
          const modelsCopy = root.querySelector('[data-co-route-models-copy]');
          if (modelsHeading) modelsHeading.textContent = 'Explore popular hearing aid styles.';
          if (modelsCopy) modelsCopy.textContent = 'Compare our featured hearing aid styles and open a product page for model details and current availability.';
        }
      } else {
        activeView = 'collection-unavailable';
        const collectionNames = {
          all: 'All products',
          'lumbar-supports': 'Lumbar supports',
          'blood-pressure-monitor': 'Blood pressure monitor',
        };
        const collectionName = collectionNames[handle] || handle.split('-').filter(Boolean).map((part) => part.charAt(0).toUpperCase() + part.slice(1)).join(' ');
        const title = root.querySelector('[data-co-route-collection-title]');
        const label = root.querySelector('[data-co-route-collection-label]');
        if (title) title.textContent = `${collectionName || 'This collection'} isn't available at this address.`;
        if (label) label.textContent = collectionName || 'SHOP CONILER';
      }
    } else if (currentPath === homePath) {
      activeView = 'home';
    }

    if (activeView === 'hearing-collection' && currentPath !== homePath) {
      const destination = `${homePath}${window.location.search}#coniler-model-comparison`;
      window.location.replace(destination);
      return;
    }

    root.querySelectorAll('[data-co-fallback-view]').forEach((view) => {
      const viewName = view.dataset.coFallbackView;
      const visible = viewName === 'home-hero' ? activeView === 'home'
        : viewName === 'hearing-collection' ? activeView === 'home' || activeView === 'hearing-collection'
          : viewName === 'home-or-hearing' ? activeView === 'home' || activeView === 'hearing-collection'
          : viewName === activeView;
      view.hidden = !visible;
    });
    if (activeView === 'home') {
      const modelsHeading = root.querySelector('#co-route-fallback-models-title');
      if (modelsHeading?.tagName === 'H1') {
        const nextHeading = document.createElement('h2');
        for (const attribute of modelsHeading.attributes) nextHeading.setAttribute(attribute.name, attribute.value);
        while (modelsHeading.firstChild) nextHeading.append(modelsHeading.firstChild);
        modelsHeading.replaceWith(nextHeading);
      }
      document.title = 'Stay close to the conversations that matter | Coniler';
      const heroImage = root.querySelector('[data-co-route-home-hero-image]');
      if (heroImage) {
        heroImage.loading = 'eager';
        heroImage.fetchPriority = 'high';
      }
    }
    root.dataset.coRouteReady = 'true';
  };

  const init = (scope = document) => {
    scope.querySelectorAll('[data-co-video-gallery]').forEach((gallery) => {
      if (gallery.dataset.coVideoReady) return;
      const track = gallery.querySelector('[data-co-video-track]');
      if (!track) return;
      gallery.dataset.coVideoReady = 'true';
      const previous = gallery.querySelector('[data-co-video-previous]');
      const next = gallery.querySelector('[data-co-video-next]');
      const updateControls = () => {
        const maxScroll = Math.max(0, track.scrollWidth - track.clientWidth);
        if (previous) previous.disabled = track.scrollLeft <= 2;
        if (next) next.disabled = track.scrollLeft >= maxScroll - 2;
      };
      const step = () => {
        const card = track.querySelector('.co-video-card');
        if (!card) return Math.max(280, track.clientWidth * 0.9);
        const gap = parseFloat(window.getComputedStyle(track).columnGap) || 0;
        return card.getBoundingClientRect().width + gap;
      };
      const scroll = (direction) => {
        const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        track.scrollBy({ left: step() * direction, behavior: reducedMotion ? 'auto' : 'smooth' });
      };
      previous?.addEventListener('click', () => scroll(-1));
      next?.addEventListener('click', () => scroll(1));
      track.addEventListener('scroll', () => window.requestAnimationFrame(updateControls), { passive: true });
      track.addEventListener('click', (event) => {
        const link = event.target.closest('[data-co-video-link]');
        if (!link) return;
        const videoId = link.dataset.coVideoId || '';
        if (!/^[A-Za-z0-9_-]{11}$/.test(videoId)) return;
        event.preventDefault();
        const iframe = document.createElement('iframe');
        iframe.src = `https://www.youtube-nocookie.com/embed/${videoId}?autoplay=1&playsinline=1&rel=0`;
        iframe.title = link.dataset.coVideoTitle || 'Coniler hearing aid video';
        iframe.allow = 'autoplay; encrypted-media; picture-in-picture; web-share';
        iframe.allowFullscreen = true;
        iframe.loading = 'eager';
        iframe.referrerPolicy = 'strict-origin-when-cross-origin';
        const videoLibrary = document.getElementById('co-video-library');
        if (videoLibrary?.contains(link)) iframe.dataset.coVideoPosterMarkup = link.outerHTML;
        link.replaceWith(iframe);
      });
      updateControls();
    });

    scope.querySelectorAll('product-info').forEach((root) => {
      const bar = root.querySelector('.co-mobile-purchase');
      const info = root.querySelector('[id^="ProductInfo-"]');
      const purchaseArea = root.querySelector('.product-form__buttons') || info;
      if (!bar || !info || !purchaseArea || bar.dataset.ready) return;
      bar.dataset.ready = 'true';
      if (!('IntersectionObserver' in window)) return;

      const visibility = new Map([[info, false], [purchaseArea, false]]);
      const updateBar = () => {
        const active = visibility.get(info) && !visibility.get(purchaseArea);
        bar.classList.toggle('co-mobile-purchase--active', active);
        bar.setAttribute('aria-hidden', String(!active));
      };
      const observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => visibility.set(entry.target, entry.isIntersecting));
        updateBar();
      }, { threshold: 0.01 });
      observer.observe(info);
      observer.observe(purchaseArea);
    });
    scope.querySelectorAll('[data-co-model-finder]').forEach((finder) => {
      if (finder.dataset.filterReady) return;
      const filters = finder.querySelector('.co-models__filters');
      const cards = [...finder.querySelectorAll('[data-co-model-card]')];
      const count = finder.querySelector('[data-co-model-count]');
      if (!filters || cards.length === 0) return;

      finder.dataset.filterReady = 'true';
      filters.hidden = false;
      const buttons = [...filters.querySelectorAll('[data-co-model-filter]')];
      const applyFilter = (value) => {
        let visible = 0;
        cards.forEach((card) => {
          const features = (card.dataset.features || '').split(/\s+/).filter(Boolean);
          const matches = value === 'all' || features.includes(value);
          card.hidden = !matches;
          if (matches) visible += 1;
        });
        buttons.forEach((button) => {
          const selected = button.dataset.coModelFilter === value;
          button.setAttribute('aria-pressed', String(selected));
          button.classList.toggle('is-active', selected);
        });
        if (count) count.textContent = value === 'all' ? `Showing all ${visible} models` : `Showing ${visible} ${visible === 1 ? 'model' : 'models'}`;
      };

      filters.addEventListener('click', (event) => {
        const button = event.target.closest('[data-co-model-filter]');
        if (button) applyFilter(button.dataset.coModelFilter);
      });
      applyFilter('all');
    });
  };
  const initSearchRelevance = () => {
    const main = document.querySelector('main[data-template="search"]');
    if (!main || main.dataset.coSearchRelevanceReady) return;
    const query = (new URLSearchParams(window.location.search).get('q') || '').trim();
    if (!query) return;
    const normalize = (value) => (value || '')
      .normalize('NFKD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLocaleLowerCase()
      .replace(/[^\p{L}\p{N}]+/gu, ' ')
      .trim();
    const stopWords = new Set(['a', 'an', 'and', 'for', 'in', 'of', 'or', 'the', 'to', 'with']);
    const queryTerms = normalize(query).split(/\s+/).filter((term) => term.length > 1 && !stopWords.has(term));
    if (!queryTerms.length) return;
    const resultCards = [...main.querySelectorAll('#product-grid [data-co-search-text]')];
    if (!resultCards.length) return;
    const hasRelevantResult = resultCards.some((card) => {
      const searchText = normalize(card.dataset.coSearchText || card.innerText || card.textContent);
      return queryTerms.every((term) => searchText.includes(term));
    });
    if (hasRelevantResult) return;
    const noMatch = main.querySelector('[data-co-search-no-match]');
    if (!noMatch) return;
    main.dataset.coSearchRelevanceReady = 'true';
    const queryLabel = noMatch.querySelector('[data-co-search-query]');
    if (queryLabel) queryLabel.textContent = `“${query}”`;
    noMatch.hidden = false;
    main.querySelector('#ProductGridContainer')?.setAttribute('hidden', '');
    main.querySelector('#main-search-filters')?.setAttribute('hidden', '');
    main.querySelector('.facets-vertical-sort')?.setAttribute('hidden', '');
    main.querySelectorAll('[data-co-search-results-status], #ProductCountDesktop, #ProductCount').forEach((status) => {
      status.textContent = `No product listings matched “${query}”.`;
    });
  };
  const bindVideoLibrary = () => {
    const dialog = document.getElementById('co-video-library');
    if (!dialog || dialog.dataset.coReady) return;
    dialog.dataset.coReady = 'true';

    const resetPlayers = () => {
      dialog.querySelectorAll('iframe[data-co-video-poster-markup]').forEach((iframe) => {
        const template = document.createElement('template');
        template.innerHTML = iframe.dataset.coVideoPosterMarkup || '';
        const poster = template.content.firstElementChild;
        if (poster) iframe.replaceWith(poster);
      });
      dialog.querySelectorAll('video').forEach((video) => {
        video.pause();
        try { video.currentTime = 0; } catch (_) { /* The browser may not have loaded metadata. */ }
        const source = video.querySelector('source');
        if (source) source.removeAttribute('src');
        video.load();
        video.hidden = true;
      });
      dialog.querySelectorAll('[data-co-hosted-video-play]').forEach((button) => { button.hidden = false; });
    };
    const closeDialog = () => {
      if (typeof dialog.close === 'function' && dialog.open) dialog.close();
      else {
        dialog.removeAttribute('open');
        resetPlayers();
      }
    };

    document.addEventListener('click', (event) => {
      const opener = event.target.closest('[data-co-video-library-open]');
      if (opener) {
        event.preventDefault();
        const drawer = opener.closest('header-drawer');
        const openMenu = drawer?.querySelector('details[open]');
        if (openMenu) openMenu.open = false;
        if (typeof dialog.showModal === 'function') dialog.showModal();
        else dialog.setAttribute('open', '');
        return;
      }
      if (event.target.closest('[data-co-video-library-close]')) closeDialog();
    });

    dialog.addEventListener('click', (event) => {
      const hostedVideoButton = event.target.closest('[data-co-hosted-video-play]');
      if (hostedVideoButton) {
        const media = hostedVideoButton.closest('.co-video-card__media');
        const video = media?.querySelector('video');
        const source = video?.querySelector('source');
        const videoUrl = hostedVideoButton.dataset.coHostedVideoSrc || '';
        if (video && source && videoUrl) {
          source.src = videoUrl;
          video.hidden = false;
          hostedVideoButton.hidden = true;
          video.load();
          const started = video.play();
          if (started?.catch) started.catch(() => {});
        }
        return;
      }
      if (event.target === dialog) closeDialog();
    });
    dialog.addEventListener('close', resetPlayers);
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => { initRouteFallback(); init(); initSearchRelevance(); }, { once: true });
  else { initRouteFallback(); init(); initSearchRelevance(); }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', bindVideoLibrary, { once: true });
  else bindVideoLibrary();
  document.addEventListener('shopify:section:load', (event) => { init(event.target); initSearchRelevance(); });
})();
